/**
 * Hallucination Fingerprint Detector
 * 
 * WHAT NOBODY ELSE IS DOING:
 * - Detects common hallucination patterns before they cause issues
 * - Maintains a fingerprint database of known AI mistakes
 * - Uses pattern matching to catch fake packages, invented APIs
 * - Learns new patterns from user feedback
 */

import * as fs from 'fs';
import * as path from 'path';
import {
  HallucinationFingerprint,
  HallucinationType,
  VerificationIssue,
} from './types';

export interface DetectionResult {
  detected: boolean;
  fingerprints: HallucinationFingerprint[];
  issues: VerificationIssue[];
  riskScore: number; // 0-100, higher = more likely hallucination
}

export class HallucinationDetector {
  private workspaceRoot: string;
  private fingerprints: HallucinationFingerprint[] = [];
  private customFingerprints: HallucinationFingerprint[] = [];
  private fingerprintPath: string;

  constructor(workspaceRoot: string) {
    this.workspaceRoot = workspaceRoot;
    this.fingerprintPath = path.join(workspaceRoot, '.guardrail', 'hallucination-fingerprints.json');
    this.initializeBuiltInFingerprints();
    this.loadCustomFingerprints();
  }

  /**
   * Initialize built-in hallucination fingerprints
   * These are common patterns that AI models frequently get wrong
   */
  private initializeBuiltInFingerprints(): void {
    this.fingerprints = [
      // Fake NPM packages - commonly hallucinated
      {
        id: 'fake-pkg-1',
        pattern: '^(react-native-async|async-hooks|node-utils|simple-fetch|easy-router|quick-db|fast-json|smart-cache|auto-format|super-logger)$',
        type: 'fake_import',
        description: 'Commonly hallucinated NPM package names',
        examples: ['react-native-async', 'node-utils', 'simple-fetch'],
        frequency: 0.15,
        falsePositiveRate: 0.02,
      },
      // Invented React hooks
      {
        id: 'fake-hook-1',
        pattern: 'use(Fetch|Data|API|Query|Request|Http|Axios|Form|Input|Modal|Dialog|Toast|Notification|Auth|User|Session|Storage|Cache|Theme|Dark|Light|Responsive|Mobile|Desktop|Scroll|Intersection|Mutation|Subscription|Socket|WebSocket|SSE|EventSource|Worker|ServiceWorker|PWA|Offline|Online|Network|Connectivity|Battery|Geolocation|Camera|Microphone|MediaRecorder|Speech|Voice|Recognition|Synthesis|Vibration|DeviceMotion|DeviceOrientation|Clipboard|Share|Bluetooth|USB|NFC|Serial|HID|Gamepad|Pointer|Gyroscope|Accelerometer|Magnetometer|AmbientLight|Proximity)[A-Z]',
        type: 'invented_api',
        description: 'Potentially invented React hook (verify it exists)',
        examples: ['useFetch', 'useData', 'useAPI'],
        frequency: 0.25,
        falsePositiveRate: 0.15, // Some of these are real libraries
      },
      // Wrong Array methods
      {
        id: 'wrong-array-1',
        pattern: '\\.(contains|remove|add|delete|insert|append|prepend|first|last|empty|clear|size)\\s*\\(',
        type: 'invented_api',
        description: 'Non-existent Array method',
        examples: ['.contains(', '.remove(', '.first('],
        frequency: 0.08,
        falsePositiveRate: 0.01,
      },
      // Wrong String methods
      {
        id: 'wrong-string-1',
        pattern: '\\.(reverse|capitalize|camelCase|snakeCase|kebabCase|count|isEmpty|isBlank|format|printf|center|ljust|rjust|zfill)\\s*\\(',
        type: 'invented_api',
        description: 'Non-existent String method',
        examples: ['.capitalize(', '.reverse(', '.isEmpty('],
        frequency: 0.06,
        falsePositiveRate: 0.01,
      },
      // Wrong Promise methods
      {
        id: 'wrong-promise-1',
        pattern: 'Promise\\.(wait|delay|sleep|timeout|retry|parallel|serial|map|filter|reduce)\\s*\\(',
        type: 'invented_api',
        description: 'Non-existent Promise static method',
        examples: ['Promise.wait(', 'Promise.delay(', 'Promise.sleep('],
        frequency: 0.04,
        falsePositiveRate: 0.01,
      },
      // Wrong Object methods
      {
        id: 'wrong-object-1',
        pattern: 'Object\\.(length|size|isEmpty|isNotEmpty|merge|deepMerge|clone|deepClone|equals|deepEquals|get|set|has|delete|forEach|map|filter)\\s*\\(',
        type: 'invented_api',
        description: 'Non-existent Object static method',
        examples: ['Object.length(', 'Object.isEmpty(', 'Object.merge('],
        frequency: 0.05,
        falsePositiveRate: 0.02,
      },
      // Wrong console methods
      {
        id: 'wrong-console-1',
        pattern: 'console\\.(write|writeln|print|println|printf|debug|verbose|notice|critical|emergency|success|failure)\\s*\\(',
        type: 'invented_api',
        description: 'Non-existent console method',
        examples: ['console.write(', 'console.print(', 'console.success('],
        frequency: 0.03,
        falsePositiveRate: 0.01,
      },
      // Wrong fs methods (Node.js)
      {
        id: 'wrong-fs-1',
        pattern: 'fs\\.(read|write|delete|remove|copy|move|rename|create|open|close|seek|tell|flush|sync)\\s*\\(',
        type: 'invented_api',
        description: 'Incorrect fs method name (check correct API)',
        examples: ['fs.read(', 'fs.write(', 'fs.delete('],
        frequency: 0.04,
        falsePositiveRate: 0.05,
      },
      // Mixing async patterns
      {
        id: 'async-mix-1',
        pattern: 'await\\s+\\w+\\.then\\s*\\(',
        type: 'pattern_violation',
        description: 'Mixing await with .then() is redundant',
        examples: ['await fetch().then('],
        frequency: 0.07,
        falsePositiveRate: 0.01,
      },
      // Wrong Express methods
      {
        id: 'wrong-express-1',
        pattern: '(app|router)\\.(send|json|render|redirect|status|header|cookie|session|body|params|query)\\s*\\(',
        type: 'invented_api',
        description: 'Wrong Express method (these are response methods)',
        examples: ['app.send(', 'router.json('],
        frequency: 0.05,
        falsePositiveRate: 0.03,
      },
      // Deprecated/removed APIs
      {
        id: 'deprecated-1',
        pattern: '(componentWillMount|componentWillReceiveProps|componentWillUpdate|getInitialState|createClass|PropTypes\\.)',
        type: 'deprecated_api',
        description: 'Deprecated React API',
        examples: ['componentWillMount', 'componentWillReceiveProps'],
        frequency: 0.06,
        falsePositiveRate: 0.01,
      },
      // Security anti-patterns
      {
        id: 'security-1',
        pattern: '(eval\\s*\\(|innerHTML\\s*=|document\\.write|new\\s+Function\\s*\\()',
        type: 'security_antipattern',
        description: 'Potential security vulnerability',
        examples: ['eval(', 'innerHTML =', 'document.write('],
        frequency: 0.03,
        falsePositiveRate: 0.01,
      },
      // Hardcoded secrets
      {
        id: 'security-2',
        pattern: '(password|secret|apiKey|api_key|token|privateKey|private_key)\\s*[=:]\\s*[\'"][^\'"]{8,}[\'"]',
        type: 'security_antipattern',
        description: 'Potential hardcoded secret',
        examples: ['password = "abc123"', 'apiKey: "sk-..."'],
        frequency: 0.04,
        falsePositiveRate: 0.05,
      },
      // Wrong TypeScript utility types
      {
        id: 'wrong-ts-1',
        pattern: '(Optional|Nullable|Mutable|Immutable|Deep|Shallow|Strict|Loose|Safe)\\s*<',
        type: 'type_mismatch',
        description: 'Non-standard TypeScript utility type',
        examples: ['Optional<', 'Nullable<', 'Mutable<'],
        frequency: 0.04,
        falsePositiveRate: 0.1, // Some libraries define these
      },
      // Wrong JSON methods
      {
        id: 'wrong-json-1',
        pattern: 'JSON\\.(encode|decode|load|dump|loads|dumps|read|write)\\s*\\(',
        type: 'invented_api',
        description: 'Non-existent JSON method (use parse/stringify)',
        examples: ['JSON.encode(', 'JSON.decode(', 'JSON.load('],
        frequency: 0.05,
        falsePositiveRate: 0.01,
      },
      // Wrong Math methods
      {
        id: 'wrong-math-1',
        pattern: 'Math\\.(sum|average|avg|mean|median|mode|variance|stddev|clamp|lerp|map|constrain)\\s*\\(',
        type: 'invented_api',
        description: 'Non-existent Math method',
        examples: ['Math.sum(', 'Math.average(', 'Math.clamp('],
        frequency: 0.04,
        falsePositiveRate: 0.01,
      },
      // Confusing Python with JavaScript
      {
        id: 'python-js-1',
        pattern: '(print\\s*\\(|len\\s*\\(|range\\s*\\(|str\\s*\\(|int\\s*\\(|float\\s*\\(|list\\s*\\(|dict\\s*\\(|tuple\\s*\\(|set\\s*\\()',
        type: 'invented_api',
        description: 'Python syntax used in JavaScript context',
        examples: ['print(', 'len(', 'range('],
        frequency: 0.03,
        falsePositiveRate: 0.02,
      },
      // Wrong event names
      {
        id: 'wrong-event-1',
        pattern: 'on(click|change|submit|focus|blur|keydown|keyup|keypress|mousedown|mouseup|mousemove|mouseenter|mouseleave|scroll|resize|load)\\s*=',
        type: 'pattern_violation',
        description: 'Event handler should use camelCase (onClick)',
        examples: ['onclick=', 'onchange='],
        frequency: 0.02,
        falsePositiveRate: 0.1, // Valid in some contexts
      },
    ];
  }

  /**
   * Load custom fingerprints from project
   */
  private loadCustomFingerprints(): void {
    try {
      if (fs.existsSync(this.fingerprintPath)) {
        const data = JSON.parse(fs.readFileSync(this.fingerprintPath, 'utf-8'));
        this.customFingerprints = data.fingerprints || [];
      }
    } catch (e) {
      console.error('Failed to load custom fingerprints:', e);
    }
  }

  /**
   * Save custom fingerprints
   */
  private saveCustomFingerprints(): void {
    try {
      const dir = path.dirname(this.fingerprintPath);
      if (!fs.existsSync(dir)) {
        fs.mkdirSync(dir, { recursive: true });
      }
      fs.writeFileSync(this.fingerprintPath, JSON.stringify({
        fingerprints: this.customFingerprints,
        lastUpdated: new Date().toISOString(),
      }, null, 2));
    } catch (e) {
      console.error('Failed to save custom fingerprints:', e);
    }
  }

  /**
   * Detect hallucinations in code
   */
  detect(code: string): DetectionResult {
    const detectedFingerprints: HallucinationFingerprint[] = [];
    const issues: VerificationIssue[] = [];
    let riskScore = 0;

    const allFingerprints = [...this.fingerprints, ...this.customFingerprints];
    const lines = code.split('\n');

    for (const fingerprint of allFingerprints) {
      try {
        const regex = new RegExp(fingerprint.pattern, 'gm');
        let match;

        while ((match = regex.exec(code)) !== null) {
          detectedFingerprints.push(fingerprint);
          
          // Find line number
          const lineNum = code.substring(0, match.index).split('\n').length;
          
          issues.push({
            type: fingerprint.type,
            severity: this.getSeverity(fingerprint),
            message: fingerprint.description,
            line: lineNum,
            evidence: match[0],
            fix: this.getFix(fingerprint, match[0]),
          });

          // Adjust risk score based on fingerprint characteristics
          const impactScore = (1 - fingerprint.falsePositiveRate) * fingerprint.frequency * 100;
          riskScore = Math.min(100, riskScore + impactScore);
        }
      } catch (e) {
        console.error(`Invalid fingerprint pattern: ${fingerprint.pattern}`, e);
      }
    }

    // Additional context-aware detection
    const contextIssues = this.detectContextualHallucinations(code);
    issues.push(...contextIssues);

    return {
      detected: detectedFingerprints.length > 0,
      fingerprints: detectedFingerprints,
      issues,
      riskScore: Math.round(riskScore),
    };
  }

  /**
   * Detect hallucinations that require context
   */
  private detectContextualHallucinations(code: string): VerificationIssue[] {
    const issues: VerificationIssue[] = [];
    const lines = code.split('\n');

    // Check for inconsistent async/await usage
    const hasAsync = code.includes('async ');
    const hasAwait = code.includes('await ');
    const hasPromise = code.includes('Promise');
    const hasThen = code.includes('.then(');

    if (hasAwait && !hasAsync) {
      const awaitLine = lines.findIndex(l => l.includes('await ')) + 1;
      issues.push({
        type: 'pattern_violation',
        severity: 'error',
        message: 'await used outside of async function',
        line: awaitLine,
        fix: 'Add async keyword to the containing function',
      });
    }

    // Check for mixed promise patterns (await + .then)
    if (hasAwait && hasThen) {
      const thenLine = lines.findIndex(l => l.includes('.then(')) + 1;
      issues.push({
        type: 'pattern_violation',
        severity: 'warning',
        message: 'Mixing async/await with .then() chains (pick one pattern)',
        line: thenLine,
        fix: 'Convert .then() chains to await for consistency',
      });
    }

    // Check for undefined variable usage (basic check)
    const variableDeclarations = new Set<string>();
    const variableUsages: Array<{ name: string; line: number }> = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Extract declarations
      const declMatch = line.match(/(?:const|let|var|function)\s+(\w+)/g);
      if (declMatch) {
        declMatch.forEach(m => {
          const name = m.split(/\s+/)[1];
          variableDeclarations.add(name);
        });
      }

      // Extract usages (simplified)
      const usageMatch = line.match(/\b([a-z_]\w*)\s*\(/gi);
      if (usageMatch) {
        usageMatch.forEach(m => {
          const name = m.replace(/\s*\($/, '');
          if (!variableDeclarations.has(name)) {
            variableUsages.push({ name, line: i + 1 });
          }
        });
      }
    }

    // Check for imports that should exist for function calls
    const builtIns = new Set([
      'console', 'Math', 'JSON', 'Date', 'Array', 'Object', 'String', 'Number',
      'Boolean', 'RegExp', 'Error', 'Promise', 'Map', 'Set', 'WeakMap', 'WeakSet',
      'Symbol', 'Proxy', 'Reflect', 'fetch', 'setTimeout', 'setInterval',
      'clearTimeout', 'clearInterval', 'parseInt', 'parseFloat', 'isNaN', 'isFinite',
      'encodeURI', 'decodeURI', 'encodeURIComponent', 'decodeURIComponent',
      'require', 'module', 'exports', '__dirname', '__filename', 'process', 'Buffer',
      'if', 'for', 'while', 'switch', 'catch', 'throw', 'return', 'new', 'class',
      'function', 'async', 'await', 'import', 'export', 'default', 'from', 'const',
      'let', 'var', 'true', 'false', 'null', 'undefined', 'this', 'super',
    ]);

    return issues;
  }

  /**
   * Get severity based on fingerprint characteristics
   */
  private getSeverity(fingerprint: HallucinationFingerprint): 'error' | 'warning' | 'info' {
    if (fingerprint.type === 'security_antipattern') return 'error';
    if (fingerprint.type === 'fake_import') return 'error';
    if (fingerprint.type === 'invented_api') return 'error';
    if (fingerprint.falsePositiveRate > 0.1) return 'info';
    if (fingerprint.frequency > 0.1) return 'warning';
    return 'warning';
  }

  /**
   * Get fix suggestion for a detected fingerprint
   */
  private getFix(fingerprint: HallucinationFingerprint, match: string): string {
    const fixes: Record<HallucinationType, string> = {
      fake_import: 'Verify the package exists on npm or correct the import path',
      invented_api: 'Check the API documentation for correct method names',
      wrong_signature: 'Check the function signature and provide correct arguments',
      type_mismatch: 'Use the correct TypeScript type',
      nonexistent_file: 'Create the file or correct the import path',
      deprecated_api: 'Update to use the modern API',
      pattern_violation: 'Follow the established coding patterns in the codebase',
      env_hallucination: 'Add the environment variable to .env file',
      route_mismatch: 'Match the route with defined API endpoints',
      schema_violation: 'Ensure data matches the defined schema',
      security_antipattern: 'Use a safer alternative to avoid security vulnerabilities',
      stale_reference: 'Update to reference current code/patterns',
    };

    return fixes[fingerprint.type] || 'Review and correct the issue';
  }

  /**
   * Add a new fingerprint learned from user feedback
   */
  addFingerprint(fingerprint: Omit<HallucinationFingerprint, 'id'>): void {
    const newFingerprint: HallucinationFingerprint = {
      ...fingerprint,
      id: `custom-${Date.now()}`,
    };
    this.customFingerprints.push(newFingerprint);
    this.saveCustomFingerprints();
  }

  /**
   * Update fingerprint frequency based on detection
   */
  updateFrequency(fingerprintId: string, detected: boolean): void {
    const fingerprint = [...this.fingerprints, ...this.customFingerprints].find(f => f.id === fingerprintId);
    if (fingerprint) {
      // Exponential moving average
      const alpha = 0.1;
      fingerprint.frequency = fingerprint.frequency * (1 - alpha) + (detected ? 1 : 0) * alpha;
    }
  }

  /**
   * Get all fingerprints for analysis
   */
  getAllFingerprints(): HallucinationFingerprint[] {
    return [...this.fingerprints, ...this.customFingerprints];
  }

  /**
   * Get high-risk fingerprints
   */
  getHighRiskFingerprints(): HallucinationFingerprint[] {
    return this.getAllFingerprints()
      .filter(f => f.frequency > 0.05 && f.falsePositiveRate < 0.1)
      .sort((a, b) => b.frequency - a.frequency);
  }
}
